import { useEffect, useRef } from 'react'
import { useMatchmaking } from '../hooks/useMatchmaking'
import {
  MATCHMAKING_TIMEOUT_MS,
  MULTIPLAYER_UI,
  type RoomInfo,
} from '../config/multiplayer'
import { t, tx } from '../i18n/translations'

interface Props {
  open: boolean
  onClose: () => void
  onMatched: (room: RoomInfo) => void
}

/**
 * ランダムマッチメイキング モーダル。
 *
 * open=true になった瞬間に useMatchmaking.start() を呼ぶ。
 * - SEARCHING 中: ローディング + 残り秒数表示 + キャンセルボタン
 * - MATCHED: onMatched(room) を呼んで閉じる
 * - TIMED_OUT / CANCELLED / ERROR: エラー表示 + 再試行 / 閉じる
 *
 * キャンセル時は onClose で閉じる（親側で useMatchmaking の reset が走らないため、
 * reset はこのコンポーネント内で管理する）。
 */
export function MatchmakingModal({ open, onClose, onMatched }: Props) {
  const { status, elapsedMs, error, room, start, cancel, reset } = useMatchmaking()
  const startedRef = useRef(false)

  // open 時に自動で start、close 時に reset
  useEffect(() => {
    if (open && !startedRef.current) {
      startedRef.current = true
      void start()
    }
    if (!open) {
      startedRef.current = false
      reset()
    }
  }, [open, start, reset])

  // MATCHED になったら親に通知
  useEffect(() => {
    if (status === 'MATCHED' && room) {
      onMatched(room)
    }
  }, [status, room, onMatched])

  // ESC キー / 背景クリックでは閉じない（意図しないキャンセルを防ぐ）。
  // 明示的にキャンセル / 閉じるボタンで操作させる。

  if (!open) return null

  const remainingMs = Math.max(0, MATCHMAKING_TIMEOUT_MS - elapsedMs)
  const remainingSec = Math.ceil(remainingMs / 1000)

  const handleCancel = async () => {
    await cancel()
    onClose()
  }

  const handleRetry = () => {
    reset()
    startedRef.current = true
    void start()
  }

  const handleClose = () => {
    // 失敗状態から閉じる（クリーンアップは reset effect に任せる）
    onClose()
  }

  return (
    <div
      className="auth-modal__overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="mm-modal-title"
    >
      <div className="auth-modal__dialog" onClick={(e) => e.stopPropagation()}>
        <header className="auth-modal__header">
          <h2 id="mm-modal-title" className="auth-modal__title">
            {status === 'SEARCHING'
              ? MULTIPLAYER_UI.matchmakingSearchingTitle
              : status === 'TIMED_OUT'
                ? MULTIPLAYER_UI.matchmakingTimeoutTitle
                : status === 'ERROR'
                  ? MULTIPLAYER_UI.matchmakingError
                  : MULTIPLAYER_UI.matchmakingSearchingTitle}
          </h2>
        </header>

        <div className="auth-modal__body">
          {status === 'SEARCHING' && (
            <>
              <p style={{ textAlign: 'center', fontSize: 14, color: 'rgba(255,255,255,0.7)' }}>
                {tx('matchmakingRemaining', { seconds: remainingSec })}
              </p>
              <div
                aria-hidden="true"
                style={{
                  margin: '12px auto',
                  textAlign: 'center',
                  fontSize: 32,
                  letterSpacing: '0.2em',
                  color: '#ffe066',
                  animation: 'introFade 1s ease-in-out infinite alternate',
                }}
              >
                ●●●
              </div>
              <div style={{ display: 'flex', justifyContent: 'center', marginTop: 12 }}>
                <button
                  type="button"
                  className="btn btn--secondary"
                  onClick={() => {
                    void handleCancel()
                  }}
                >
                  {MULTIPLAYER_UI.matchmakingCancel}
                </button>
              </div>
            </>
          )}

          {status === 'TIMED_OUT' && (
            <>
              <p className="auth-modal__reason">
                {t('matchmakingTimeoutMessage')}
              </p>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
                <button
                  type="button"
                  className="btn btn--primary"
                  onClick={handleRetry}
                >
                  {MULTIPLAYER_UI.matchmakingRetry}
                </button>
                <button
                  type="button"
                  className="btn btn--secondary"
                  onClick={handleClose}
                >
                  {MULTIPLAYER_UI.matchmakingClose}
                </button>
              </div>
            </>
          )}

          {status === 'ERROR' && (
            <>
              <div className="auth-modal__error" role="alert">
                <p>{MULTIPLAYER_UI.matchmakingError}</p>
                {error && <p className="auth-modal__error-detail">{error}</p>}
              </div>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
                <button
                  type="button"
                  className="btn btn--primary"
                  onClick={handleRetry}
                >
                  {MULTIPLAYER_UI.matchmakingRetry}
                </button>
                <button
                  type="button"
                  className="btn btn--secondary"
                  onClick={handleClose}
                >
                  {MULTIPLAYER_UI.matchmakingClose}
                </button>
              </div>
            </>
          )}

          {status === 'CANCELLED' && (
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <button
                type="button"
                className="btn btn--secondary"
                onClick={handleClose}
              >
                {MULTIPLAYER_UI.matchmakingClose}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
